import React from 'react';
import { Select as BaseUISelect, type SelectRootChangeEventDetails } from '@base-ui/react/select';
import classNames from 'classnames';
import Icon from '../icon';
import { SelectCreateRow } from './select-create-row';
import {
  StyledIcon,
  StyledItem,
  StyledList,
  StyledPopup,
  StyledPositioner,
  StyledSeparator,
  StyledTrigger,
  StyledValue,
} from './styles';

export type SelectVariant = 'default' | 'clean';
export type SelectSize = 'xs' | 'sm' | 'md' | 'lg';

export type SelectOption<Value extends string = string> = {
  label: React.ReactNode;
  value: Value;
  disabled?: boolean;
};

const INTERNAL_CLEAR_VALUE = '__internal_clear__' as const;
type InternalValue<Value extends string> = Value | typeof INTERNAL_CLEAR_VALUE;

export type SelectProps<Value extends string = string> = {
  /** Array of options to display in the dropdown */
  options: SelectOption<Value>[];
  /** Currently selected value */
  value?: Value;
  /**
   * Callback fired when selection changes. `eventDetails` is supplied by Base UI
   * for real user selections and omitted for synthetic changes (e.g. after create).
   */
  onChange?: (value: Value | undefined, eventDetails?: SelectRootChangeEventDetails) => void;
  /** Placeholder text shown when no value is selected */
  placeholder?: React.ReactNode;
  /**
   * Optional render function for the trigger's selected value display.
   * When omitted, the trigger shows the matching option's `label` as-is.
   * Use this when option `label` JSX (e.g. with adornments like icons or tooltips)
   * shouldn't appear inside the trigger.
   */
  renderTriggerValue?: (value: Value | null) => React.ReactNode;
  /**
   * Whether the select is disabled. Note: a Select with no `options` is also disabled
   * automatically (nothing to pick), unless an inline `create` footer is provided.
   */
  disabled?: boolean;
  /**
   * Visual variant of the select trigger
   * - 'default': Has border and background
   * - 'clean': Borderless with bottom border on focus
   * @default 'default'
   */
  variant?: SelectVariant;
  /**
   * Size of the select trigger and dropdown items
   * - 'sm': Small text and compact height
   * - 'md': Medium text size and height
   * - 'lg': Large text size and height (default, 16px)
   * @default 'lg'
   */
  size?: SelectSize;
  /** Additional CSS class for the trigger */
  className?: string;
  /** Minimum width for the trigger button (e.g., '200px', '12rem'). */
  minWidth?: string;
  /**
   * When true, the trigger fills 100% of its parent's width.
   * Use for Selects inside form fields/columns that should span the layout.
   */
  block?: boolean;
  id?: string;
  'data-name'?: string;
  'data-testid'?: string;
  'aria-label'?: string;
  /** ID of element that labels this select */
  'aria-labelledby'?: string;
  /** Indicates validation state for accessibility */
  'aria-invalid'?: boolean | 'true' | 'false' | 'grammar' | 'spelling';
  /**
   * When true, adds a clear option at the top of the dropdown
   * Selecting it will reset the selection and call onClear
   */
  allowClear?: boolean;
  /**
   * Label text for the clear option (default: 'None')
   * @default 'None'
   */
  allowClearText?: string;
  /** Callback fired when the clear option is selected */
  onClear?: () => void;
  /**
   * When true, uses a modal overlay instead of a popover
   * Modal mode blocks interaction with the rest of the page until dismissed
   * @default false
   */
  modal?: boolean;

  /**
   * When true, renders the dropdown in a portal
   * This is useful when the Select is used within containers that have overflow hidden or z-index issues
   * @default true
   */
  shouldRenderInPortal?: boolean;

  /**
   * When set, renders an inline "+ Add new" footer inside the popup.
   * Absence of `create` disables the footer.
   */
  create?: SelectCreateConfig<Value>;
};

export type SelectCreateConfig<Value extends string = string> = {
  /**
   * Called when the user submits the inline create input. Return the value to
   * select after creation (typically the new item's id). Return a Promise for
   * the Create button to show a loading state — with react-query, use
   * `mutateAsync`, not `mutate`.
   */
  onCreate: (name: string) => Value | Promise<Value>;
  /** Footer button label. Defaults to "Add new". */
  label?: string;
  /** Placeholder for the inline create input. */
  placeholder?: string;
  /** Create button label. Defaults to "Create". */
  buttonLabel?: string;
  /** Validate the new-value draft. Returns an error message to block submission, or null when valid. */
  validate?: (draft: string) => string | null;
};

function Select<Value extends string = string>({
  options,
  value,
  onChange,
  placeholder = 'Select an option',
  renderTriggerValue,
  disabled = false,
  variant = 'default',
  size = 'lg',
  className,
  minWidth,
  block = false,
  id,
  'data-name': dataName,
  'data-testid': dataTestId,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledby,
  'aria-invalid': ariaInvalid,
  allowClear = false,
  allowClearText = 'None',
  onClear,
  modal = false,
  shouldRenderInPortal = true,
  create,
}: SelectProps<Value>) {
  const [portalContainer, setPortalContainer] = React.useState<HTMLElement | undefined>(undefined);
  const [open, setOpen] = React.useState(false);

  // An options-less Select has nothing to pick, so render it disabled — unless an inline `create`
  // footer is available (then the user can still add a value), or the caller already disabled it.
  const isDisabled = disabled || (options.length === 0 && !create);

  const items: { label: React.ReactNode; value: InternalValue<Value>; disabled?: boolean }[] =
    allowClear && options.length > 0 ? [{ label: allowClearText, value: INTERNAL_CLEAR_VALUE }, ...options] : options;

  const handleValueChange = (newValue: InternalValue<Value> | null, eventDetails: SelectRootChangeEventDetails) => {
    if (newValue === INTERNAL_CLEAR_VALUE) {
      onClear?.();
      onChange?.(undefined, eventDetails);
    } else if (newValue !== null) {
      onChange?.(newValue as Value, eventDetails);
    } else {
      // Base UI passes null when value is cleared
      onChange?.(undefined, eventDetails);
    }
  };

  // Auto-detect if Select is inside a modal and portal to that container instead of document.body.
  // This keeps the dropdown within the modal's z-index stacking context so the overlay doesn't block it.
  const setTriggerRef = (node: HTMLButtonElement | null) => {
    if (!node) {
      return;
    }

    const modalContainer = node.closest('[data-slot="modal-content"]');
    setPortalContainer(modalContainer instanceof HTMLElement ? modalContainer : undefined);
  };

  const handleCreated = (newValue: Value) => {
    onChange?.(newValue);
    setOpen(false);
  };

  const PortalComponent = shouldRenderInPortal ? BaseUISelect.Portal : React.Fragment;
  return (
    <BaseUISelect.Root
      value={value ?? null}
      onValueChange={handleValueChange}
      disabled={isDisabled}
      modal={modal}
      items={items}
      open={open}
      onOpenChange={setOpen}
    >
      <StyledTrigger
        ref={setTriggerRef}
        className={classNames('select-trigger', 'nodrag', className)}
        variant={variant}
        size={size}
        $minWidth={minWidth}
        $block={block}
        id={id}
        data-name={dataName}
        data-testid={dataTestId || 'select'}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledby}
        aria-invalid={ariaInvalid}
      >
        <StyledValue placeholder={placeholder}>
          {renderTriggerValue
            ? (currentValue: Value | null) => renderTriggerValue(currentValue) ?? placeholder
            : undefined}
        </StyledValue>
        <StyledIcon>
          <Icon
            name={'CHEVRON_DOWN'}
            variant={isDisabled ? 'gray600' : 'gray900'}
            size={size}
            className={'trigger-icon'}
          />
        </StyledIcon>
      </StyledTrigger>

      <PortalComponent container={portalContainer}>
        <StyledPositioner
          sideOffset={4}
          alignItemWithTrigger={false}
          positionMethod={'fixed'}
          className={'select-positioner'}
        >
          <StyledPopup variant={variant} className={'select-popup'}>
            <StyledList>
              {items.map(option => (
                <StyledItem
                  key={option.value}
                  value={option.value}
                  disabled={option.disabled}
                  size={size}
                  data-testid={`select-option-${option.value}`}
                >
                  <BaseUISelect.ItemText className={'item-text'}>{option.label}</BaseUISelect.ItemText>
                </StyledItem>
              ))}
            </StyledList>
            {create && (
              <>
                <StyledSeparator />
                <SelectCreateRow<Value>
                  onCreate={create.onCreate}
                  onCreated={handleCreated}
                  createLabel={create.label ?? 'Add new'}
                  createPlaceholder={create.placeholder}
                  createButtonLabel={create.buttonLabel ?? 'Create'}
                  createNewValidate={create.validate}
                />
              </>
            )}
          </StyledPopup>
        </StyledPositioner>
      </PortalComponent>
    </BaseUISelect.Root>
  );
}

export default Select;
